import { Controller } from "@hotwired/stimulus"
import { useDebounce } from "stimulus-use"

const MEDIUM_BREAKPOINT = 992

const MAX_ZOOM_BY_QUERY_TYPE = {
  "tourist_attraction": 15,
  "point_of_interest": 15,
  "establishment": 15,
  "premise": 15,
  "route": 15,
  "street_address": 15,
  "sublocality": 14,
  "neighborhood": 14,
  "locality": 13,
  "postal_code": 13,
  "administrative_area_level_2": 10,
  "administrative_area_level_1": 9,
  "country": 7,
}

export const PIN_COLORS = {
  "default": "#221644",
  "pinned": "#AE92FF",
  "hover": "#FFD71C",
}

  // Custom marker icon
export const markerIcon = (circleColor = PIN_COLORS["default"]) => {
  const parser = new DOMParser()
  const markerIconString = `
    <svg
      width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
      style="filter: drop-shadow(0px 3px 4px rgba(0, 0, 0, 0.4)); transform: translateY(50%);"
    >
      <circle cx="12" cy="12" r="12" fill="white"/>
      <circle cx="12" cy="12" r="5" fill="${circleColor}"/>
    </svg>
  `
  const element = parser.parseFromString(markerIconString, "image/svg+xml").documentElement

  return element
}

/**
 *
 * This controller manages the product search functionality.
 * It intercepts changes made to the filters directly in the main form or in the subcomponents managed by the outlets.
 * With each change, it updates the URL parameters and submits the form to get the corresponding results.
 * It also handles the connection between the search form and the Google Maps.
 **/
export default class extends Controller {
  static values = { isAdmin: Boolean }
  static debounces = [
    { name: "submitForm", wait: 500 },
    { name: "submitFormWithDebounce", wait: 1000 },
  ]
  static outlets = ["navbar-search-form", "search-filter-bar"]

  async initialize() {
    this.submitForm()
    await google.maps.importLibrary("maps")
  }

  connect() {
    useDebounce(this)
    this.markers = []
    this.trackedProductList = []
    this.queryInfoMarker = null
    this.initMap()

    // Initialize search tracking
    this.trackedProductList = []
    this.trackedMapMarkerPinsList = []
    this.hasTrackedMapMoved = false

    this.fireTurboRequestOnPopState()
  }

  // Create map marker & infoWindow when the productInfoWindow is added to the DOM
  async productInfoWindowTargetConnected(target) {
    await google.maps.importLibrary("marker")
    this.addMarker(target)

    // Once all the productInfoWindows are added to the DOM, fit the map bounds
    if (this.markers.length == this.productInfoWindowTargets.length) {
      this.fitBounds()

      // if an infoWindow is already open and its product still present, update its content
      // else close the infoWindow
      if (this.infoBox) {
        const infoBoxNewMarker = this.markers.find(
          (marker) =>
            marker[0].position.lat == this.infoBox.position_.lat &&
            marker[0].position.lng == this.infoBox.position_.lng
        )

        if (infoBoxNewMarker) {
          const productInfoWindow = infoBoxNewMarker[1]
          this.infoBox.setContent(productInfoWindow.children[0].cloneNode(true))
        } else {
          this.infoBox.close()
          this.infoBox = null
        }
      }
    }
  }

  productListItemTargetConnected() {
    // Kill residual tooltips that may be left behind when hovering one while turbo request is in progress
    $(".tooltip").tooltip("hide")
  }

  // Remove map marker when the productInfoWindow is removed from the DOM
  async productInfoWindowTargetDisconnected(target) {
    await google.maps.importLibrary("marker")
    const markerContainer = this.markers.find((e) => e[1] == target)
    const marker = markerContainer[0]
    const index = this.markers.indexOf(markerContainer)
    marker.setMap(null)
    this.markers.splice(index, 1)
  }

  // Add event listeners to the search form inputs
  // When the user changes the search form inputs, push the form parameters to the URL and submit the form
  async formTargetConnected(target) {
    Array.from(target.elements).forEach((input) => {
      input.addEventListener("change", () => {
        this.pushFormParamsToUrl()
        this.submitFormFromModal()
      })
    })
  }

  async noResultsMessageTargetConnected() {
    this.fitBounds()
  }

  queryInfoTargetConnected() {
    this.setQueryInfoMarker()
  }

  queryInfoTargetDisconnected(target) {
    if (target.dataset.showMarker == "false") return

    if (this.queryInfoMarker) {
      this.queryInfoMarker.setMap(null)
    }
  }

  // Initialize the map
  async initMap() {
    const centerPoint = new google.maps.LatLng(
      this.centerPointTarget.dataset.lat,
      this.centerPointTarget.dataset.lng
    )
    var zoom = parseInt(this.formTarget.elements.zoom.value) || 7
    this.map = new google.maps.Map(this.mapTarget, this.mapOptions(centerPoint, zoom))
    this.addMapListeners()
  }

  async setQueryInfoMarker() {
    if (this.queryInfoTarget.dataset.showMarker == "false") return

    await google.maps.importLibrary("marker")
    this.queryInfoMarker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: {
        lat: parseFloat(this.queryInfoTarget.dataset.lat),
        lng: parseFloat(this.queryInfoTarget.dataset.lng),
      },
      content: markerIcon(PIN_COLORS["pinned"]),
    })
  }

  // Add event listeners to the map
  // dragend: when the map is dragged by the user, set the moved_map input to true
  // zoom_changed: when the map is zoomed by the user, set the moved_map input to true
  // idle: when the map stops moving, update the search form with the new bounds of the map and submit the form
  // mapMovedByUser: boolean to prevent the search form from submitting twice
  // boundsFitted: permit to ignore listeners when the map bounds are fitted
  // tracking Map Moved event in activity_search_controller.js and venue_search_controller.js
  addMapListeners() {
    this.mapMovedByUser = false
    this.boundsFitted = false

    google.maps.event.addListener(this.map, "click", () => {
      this.infoBox?.close()
    })

    google.maps.event.addListener(this.map, "dragend", () => {
      this.formTarget.elements.moved_map.value = true
      this.mapMovedByUser = true
    })

    google.maps.event.addListener(this.map, "zoom_changed", () => {
      this.formTarget.elements.zoom.value = this.map.getZoom()
      if (this.boundsFitted) return

      this.formTarget.elements.moved_map.value = true
      this.mapMovedByUser = true
    })
  }

  // Frame the map around the product markers on the page
  async fitBounds() {
    if (this.formTarget.elements.moved_map.value == "true") return

    const query = this.formTarget.elements.query.value
    const googlePlaceType = this.formTarget.elements.google_place_type.value
    const maxZoomOnFitBounds =
      query && googlePlaceType ? MAX_ZOOM_BY_QUERY_TYPE[googlePlaceType] : 9

    this.bounds = new google.maps.LatLngBounds()
    this.productInfoWindowTargets.forEach((productInfoWindow) => {
      this.bounds.extend({
        lat: parseFloat(productInfoWindow.dataset.lat),
        lng: parseFloat(productInfoWindow.dataset.lng),
      })
    })

    if (this.hasQueryInfoTarget) {
      this.bounds.extend({
        lat: parseFloat(this.queryInfoTarget.dataset.lat),
        lng: parseFloat(this.queryInfoTarget.dataset.lng),
      })
    }

    this.boundsFitted = true
    this.map.fitBounds(this.bounds)
    this.map.setZoom(Math.min(this.map.getZoom(), maxZoomOnFitBounds))
  }

  scrollResultsToTop() {
    this.productListTarget.scrollTo({ top: 0 }) // Desktop view
    window.scrollTo({ top: 0 }) // Mobile view
  }

  // Create a marker on the map for a productInfoWindow.
  // Adds an infoWindow to the marker that displays the productInfoWindow.
  // Changes the color of the marker when the productInfoWindow or the marker itself is hovered.
  addMarker(productInfoWindow) {
    const pinned = productInfoWindow.dataset.pinned == "true"
    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: this.map,
      position: {
        lat: parseFloat(productInfoWindow.dataset.lat),
        lng: parseFloat(productInfoWindow.dataset.lng),
      },
      content: markerIcon(),
      zIndex: pinned ? 2 : 1,
    })

    if (pinned) {
      marker.content.children[1].style.fill = PIN_COLORS["pinned"]
    }

    this.addInfoWindow(marker, productInfoWindow)
    this.markers.push([marker, productInfoWindow])

    marker.element.addEventListener("mouseenter", () => {
      this.toggleMarkerIconHovering(marker, true, pinned)
    })

    marker.element.addEventListener("mouseleave", () => {
      this.toggleMarkerIconHovering(marker, false, pinned)
    })

    productInfoWindow.previousElementSibling.addEventListener("mouseover", () => {
      this.toggleMarkerIconHovering(marker, true, pinned)
    })

    productInfoWindow.previousElementSibling.addEventListener("mouseout", () => {
      this.toggleMarkerIconHovering(marker, false, pinned)
    })
  }

  // // Add an infoWindow to a marker that displays a productInfoWindow
  // // Close the infoWindow if another marker is clicked
  // addInfoWindow(marker, productInfoWindow) {
  //   // define in activity_search_controller.js and venue_search_controller.js
  // }

  // Reset the address elements tracking when the map is moved
  resetFormElementAddress() {
    this.formTarget.elements.query.value = null
    this.formTarget.elements.address_establishment.value = null
    this.formTarget.elements.address_locality.value = null
    this.formTarget.elements.address_administrative_area_level_1.value = null
    this.formTarget.elements.address_administrative_area_level_2.value = null
    this.formTarget.elements.address_country.value = null
    this.formTarget.elements.address_zipcode.value = null
  }

  // Update the search form with the new bounds of the map and submit the form
  searchFromMap() {
    this.formTarget.elements.bounds_sw_lat.value = this.map.getBounds().getSouthWest().lat()
    this.formTarget.elements.bounds_sw_lng.value = this.map.getBounds().getSouthWest().lng()
    this.formTarget.elements.bounds_ne_lat.value = this.map.getBounds().getNorthEast().lat()
    this.formTarget.elements.bounds_ne_lng.value = this.map.getBounds().getNorthEast().lng()

    this.pushFormParamsToUrl()

    // prevents the form from submitting twice when fitBounds is called
    if (this.boundsFitted) {
      this.boundsFitted = false
    } else {
      this.submitForm()
    }
  }

  // Push the form parameters to the URL
  pushFormParamsToUrl() {
    const url = new URL(location)
    url.search = ""
    Array.from(this.formTarget.elements)
      .filter((e) => e.type != "submit")
      .forEach((e) => {
        const isRadioCheckbox = ["checkbox", "radio"].includes(e.type)
        const isValidRadioCheckbox = isRadioCheckbox && e.checked
        const isIgnoredHidden = e.type == "hidden" && e.value == "0" && !e.checked
        const shouldAppendParam =
          !isIgnoredHidden && (isValidRadioCheckbox || !isRadioCheckbox) && !!e.value
        const orderingByPrice =
          e.name == "order_by" && ["price_asc", "price_desc"].includes(e.value)

        // remove the order_by param if the user is ordering by price and the package filter is not selected
        if (orderingByPrice && !this.formTarget.elements.package.value) e.value = null

        if (shouldAppendParam) url.searchParams.append(e.name, e.value)
      })
    history.pushState({}, "", url)
  }

  submitForm() {
    this.formTarget.requestSubmit()
  }

  submitFormWithDebounce() {
    this.formTarget.requestSubmit()
  }

  submitFormFromModal() {
    this.isAdminValue ? this.submitFormWithDebounce() : this.submitForm()
  }

  changeOrderBy(e) {
    let orderBy = e.target.value
    this.formTarget.elements.order_by.value = orderBy
    this.pushFormParamsToUrl()
    this.submitForm()
  }

  // Ensure the modal body scrolls to the top when the modal is opened
  scrollModalBodyToTop() {
    setTimeout(() => {
      this.modalBodyTarget.scrollTo({ top: 0 })
    }, 200)
  }

  togglePackageFilter(e) {
    if (window.innerWidth < MEDIUM_BREAKPOINT) {
      this.mobileFilterButtonTarget.click()
    } else {
      this.packagesPopoverButtonTarget.click()
    }
    // track("Clicked on Page", { name: "Display total price"  })
  }

  // This allows to use the previous and next buttons of the browser.
  // The turbo request over a location.reload() permits to keep the map state.
  fireTurboRequestOnPopState() {
    window.addEventListener("popstate", function () {
      fetch(location.href, {
        method: "GET",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        },
      })
        .then((r) => r.text())
        .then((html) => Turbo.renderStreamMessage(html))
    })
  }

  toggleMarkerIconHovering(marker, isHovered, isPinned) {
    if (isHovered) {
      marker.content.children[1].style.fill = PIN_COLORS["hover"]
      marker.content.style.width = 28
      marker.content.style.height = 28
      marker.zIndex = 3
    } else {
      marker.content.children[1].style.fill = PIN_COLORS[isPinned ? "pinned" : "default"]
      marker.content.style.width = 24
      marker.content.style.height = 24
      marker.zIndex = isPinned ? 2 : 1
    }
  }

  // Configure the map options
  mapOptions(centerPoint, zoom = 7) {
    const optionHash = {
      center: centerPoint,
      zoom: zoom,
      mapId: "3fe2568bfd28a7e1",
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_TOP,
      },
    }

    return optionHash
  }

  isScrolledIntoView(el) {
    const rectElement = el.getBoundingClientRect()
    return rectElement.top >= 0 && rectElement.bottom <= window.innerHeight
  }
}
